import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box/Box';
import Paper from '@mui/material/Paper/Paper';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Chip from '@mui/material/Chip/Chip';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Switch from '@mui/material/Switch/Switch';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import { useHackathon } from '../../hooks/useHackathon';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useAuth } from '../../services/useAuth';
import { disableVotingPeriod, enableVotingPeriod } from '../../services/hackathon.service';
import { SessionPayload } from '../../services/session.service';
import { hasAnyRole, sessionRoles, voteEligibility } from '../../utils/sessions';
import { LiveDot } from '../Sessions/LiveNowStrip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMyVotes } from '../../hooks/useMyVotes';
import { voteKey } from '../../services/vote.service';

/**
 * One-line "what's on now" bar for hackathons with sessions, built to fit a phone screen:
 * live dot, room, team, and a Vote button only when the viewer can vote. Organizers also get the
 * hackathon-wide voting switch. Renders nothing for participants when no room is live.
 */
export const SessionLiveBanner = ({ hackathonId, sessions }: { hackathonId: string, sessions: SessionPayload[] }) => {
    const { hackathon, reload } = useHackathon(hackathonId);
    const { sessions: live, votingPeriodOn, resultsAnnounced } = useLiveBoard(hackathonId);
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    // the team on stage is either still to score or done: the button says which
    const scored = useMyVotes(user ? hackathonId : undefined);

    // once results are announced nothing is on stage any more, even if the room was never finished
    if (!hackathon || hackathon.winners?.length || resultsAnnounced) {
        return null;
    }
    const email = user ? user.email : undefined;
    const isOrganizer = !!user && hackathon.organizers.some(o => o.email === user.email);
    const votingOn = votingPeriodOn;
    const single = sessions.length === 1;

    const sessionFor = (id: string) => sessions.find(s => s._id === id);
    const liveRooms = live.filter(room => room.activeIdea && sessionFor(room._id));
    const mine = (id: string) => {
        const session = sessionFor(id);
        return !!session && hasAnyRole(sessionRoles(session, hackathon, email));
    };
    const ordered = [...liveRooms.filter(r => mine(r._id)), ...liveRooms.filter(r => !mine(r._id))];
    const room = ordered[0];

    if (!room && !isOrganizer) {
        return null;
    }

    const session = room ? sessionFor(room._id)! : undefined;
    const team = room ? hackathon.ideas.find(i => i._id === room.activeIdea) : undefined;
    const canVote = !!(room && session && team && voteEligibility(hackathon, session, room, team._id, email, { single, votingPeriodOn: votingOn }).allowed);
    const roomLink = room ? `/h/${hackathon.slug}/s/${room.slug}` : '';

    const toggleVoting = () => {
        setBusy(true);
        (votingOn ? disableVotingPeriod : enableVotingPeriod)(hackathon._id)
            .then(() => reload && reload())
            // the live board picks up the switch from the response; fetch in case another page polls it
            .catch(err => console.error(err))
            .finally(() => setBusy(false));
    };

    return (
        <Paper
            component="section"
            aria-label="Live now"
            elevation={1}
            sx={{
                position: 'sticky',
                top: 72,
                zIndex: theme => theme.zIndex.appBar - 1,
                borderRadius: '14px',
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 48
            }}>
            {
                room && session && team ? (
                    <Box
                        component={Link}
                        to={roomLink}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                            flex: 1,
                            color: 'text.primary',
                            textDecoration: 'none'
                        }}>
                        <LiveDot />
                        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                            <b>{session.name}:</b> {team.title}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                        No room is presenting
                    </Typography>
                )
            }
            {
                ordered.length > 1 && (
                    <Tooltip title={`${ordered.length - 1} more room${ordered.length > 2 ? 's' : ''} live`}>
                        <Chip
                            size="small"
                            label={`+${ordered.length - 1}`}
                            component={Link}
                            to={`/h/${hackathon.slug}`}
                            clickable
                            aria-label={`${ordered.length - 1} more rooms live`} />
                    </Tooltip>
                )
            }
            {
                canVote && (scored.has(voteKey(team!._id, session!._id))
                    ? <Button size="small" variant="outlined" color="success" startIcon={<CheckCircleIcon />} component={Link} to={`${roomLink}#voting`} sx={{ flexShrink: 0 }}
                        aria-label={`Scored ${team!.title}`}>
                        Scored
                    </Button>
                    : <Button size="small" variant="contained" component={Link} to={`${roomLink}#voting`} sx={{ flexShrink: 0 }}
                        aria-label={`Score ${team!.title} now`}>
                        Score now
                    </Button>)
            }
            {
                isOrganizer && (
                    // with one room this is the very same switch as the room's "Voting open", so it carries the same name
                    <Tooltip title={single
                        ? 'Opens and closes voting for everyone. Same switch as "Voting open" in the room.'
                        : 'Hackathon-wide voting. Rooms only take votes while this is on.'}>
                        <FormControlLabel
                            sx={{ flexShrink: 0, mr: 0, ml: 0.5 }}
                            control={<Switch size="small" checked={votingOn} disabled={busy} onChange={toggleVoting} inputProps={{ 'aria-label': single ? 'Voting open' : 'Hackathon-wide voting' }} />}
                            // on phones the switch alone, so the team name keeps the space
                            label={<Typography variant="body2" sx={{ display: { xs: 'none', sm: 'inline' } }}>{votingOn ? 'Voting open' : 'Voting closed'}</Typography>} />
                    </Tooltip>
                )
            }
        </Paper>
    );
}
